import classNames from 'classnames'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useContext } from 'react'
import { motion } from 'framer-motion'

import { CalculatorSolarContext } from 'context/CalculatorSolarContext'

import { Logo } from '../Logo'
import { Finished } from './components/Steps/Finished'
import { PreLead } from './components/Steps/PreLead'
import { Summary } from './components/Steps/Summary'
import { useScrollTo } from '@hooks/use-scroll-to'
import { HomeButton } from '@components/home/components/home-button'
import { BlockCall } from '@services/api/block-calls/types'

interface Props {
  windowWidth?: number
  title?: ReactNode
  logo?: ReactNode
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p'
  imageCropCenter?: boolean
  disableSnap?: boolean
  desktopImages?: BlockCall[]
  mobileImages?: BlockCall[]
  compactMode?: boolean
}

const CALCULADORA_SOLAR_PATH = '/calculadora-solar'

export const SimulationBanner: FC<Props> = ({
  windowWidth,
  title,
  logo,
  headingLevel,
  imageCropCenter = false,
  disableSnap = false,
  desktopImages = [],
  mobileImages = [],
  compactMode: compactModeProp
}) => {
  const router = useRouter()
  const { currentStep } = useContext(CalculatorSolarContext)

  const compactMode =
    compactModeProp ?? router.pathname !== CALCULADORA_SOLAR_PATH

  const stepsData = {
    simulação: <PreLead headingLevel={headingLevel} />,
    resumo: <Summary headingLevel={headingLevel} compactMode={compactMode} />,
    finished: <Finished headingLevel={headingLevel} />
  } as const

  const { scrollToElementWithId } = useScrollTo()

  const handleClick = useCallback(() => {
    scrollToElementWithId(
      windowWidth && windowWidth < 768 ? 'simulation' : 'hero-banner'
    )
  }, [windowWidth, scrollToElementWithId])

  const activeDesktopImage = desktopImages?.[0]
  const activeMobileImage = mobileImages?.[0]

  const finalBackground =
    activeDesktopImage?.imageUrl ||
    '/assets/v2/bg-simulation-banner-desktop.png'
  const finalBackgroundAlt = activeDesktopImage?.imageAlt || 'calculadora solar'
  const finalBackgroundMobile = activeMobileImage?.imageUrl || finalBackground
  const finalBackgroundMobileAlt =
    activeMobileImage?.imageAlt || finalBackgroundAlt

  const showLeftSide = currentStep !== 'resumo'

  return (
    <section
      id="hero-banner"
      className={classNames(
        'relative w-full overflow-hidden',
        // altura estável no mobile e no desktop
        'min-h-[560px] sm:min-h-[620px] md:min-h-[650px]',
        {
          'md:snap-always md:snap-start': !disableSnap
        }
      )}
    >
      {/* Background Desktop */}
      <Image
        src={finalBackground}
        alt={finalBackgroundAlt}
        fill
        priority
        quality={100}
        className={classNames(
          'hidden md:block -z-20 object-cover',
          imageCropCenter ? 'object-center' : 'object-center'
        )}
      />

      {/* Background Mobile */}
      <Image
        src={finalBackgroundMobile}
        alt={finalBackgroundMobileAlt}
        fill
        priority
        quality={100}
        className="md:hidden -z-20 object-cover object-center"
      />

      {/* overlay leve opcional para legibilidade no mobile (se já tiver no bg, pode remover) */}
      <div className="absolute inset-0 -z-10 md:hidden bg-black/30" />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
          {/* Lado esquerdo (logo + título): oculto no resumo só na página calculadora-solar (não em compactMode) */}
          <div
            className={classNames(
              'w-full',
              'pt-16 sm:pt-20 md:pt-0',
              currentStep === 'resumo' && !compactMode
                ? 'hidden'
                : {
                    'hidden lg:block': !showLeftSide,
                    block: showLeftSide,
                    'snap-always snap-start md:snap-align-none': !disableSnap
                  }
            )}
          >
            <div className="flex flex-col">
              {/* Logo: se vier via props, usa. Se não vier, usa <Logo />. Se for null, não renderiza */}
              {logo !== null && (
                <div className="mb-4 md:mb-6">
                  {logo ?? (
                    <div className="w-[180px] h-[65px] sm:w-[200px] sm:h-[72px] md:w-[179px] md:h-[65px] lg:w-[209px] lg:h-[76px] xl:w-[332px] xl:h-[121px]">
                      <Logo />
                    </div>
                  )}
                </div>
              )}

              {/* Title: se vier via props, usa. Se não vier, usa default */}
              <div className="w-full">
                {title ?? (
                  <h2
                    className="
                      mx-auto md:mx-0
                      max-w-[22ch] md:max-w-none
                      text-[28px] leading-[34px]
                      sm:text-[32px] sm:leading-[38px]
                      md:text-[40px] md:leading-[46px]
                      lg:text-[44px] lg:leading-[52px]
                      xl:text-[50px] xl:leading-[60px]
                      font-normal tracking-wide text-white
                      drop-shadow-[0px_4px_4px_rgba(0,0,0,0.25)]
                      md:drop-shadow-none
                    "
                  >
                    Economize na{' '}
                    <span className="font-bold text-v3-orange-325 lg:text-v3-orange-375">
                      conta de luz
                    </span>{' '}
                    <span className="block md:inline">com a marca líder</span>{' '}
                    <span className="block md:inline">do mercado</span>
                  </h2>
                )}
              </div>

              {/* CTA mobile (se quiser manter) */}
              <div className="mt-8 md:hidden">
                <HomeButton onClick={handleClick} variant="outline-secondary">
                  FAÇA UMA SIMULAÇÃO
                </HomeButton>
              </div>
            </div>
          </div>

          {/* Card/Form (Steps) */}
          <motion.div
            layout
            id="container"
            transition={{ duration: 0.6 }}
            style={{ borderRadius: 16 }}
            className={classNames(
              // no mobile: ocupa largura total e não “estoura” altura
              'w-full my-10',
              // no desktop: mantém suas medidas
              'md:flex md:justify-end md:max-w-[360px] md:h-[600px] md:overflow-hidden',
              'lg:h-[614px] lg:justify-end xl:justify-center xl:h-[461px] 1.5xl:h-[614px]',
              {
                'lg:max-w-[452px] xl:max-w-[360px] 1.5xl:max-w-[452px]':
                  currentStep !== 'resumo',
                'lg:max-w-full xl:max-w-full': currentStep === 'resumo'
              }
            )}
          >
            {stepsData[currentStep]}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
